import React from 'react';
import { useTranslation } from 'react-i18next';

import { keyBy, sortBy } from 'lodash';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faComment, faTrophy } from '@fortawesome/pro-light-svg-icons';

import { Slider } from 'components/controls/Slider';

import { getOptionScore, getItemScore, getScoreColor } from '../../../utils/ranking';
import { faComment as faCommentPlain, faHeart } from '@fortawesome/pro-solid-svg-icons';
import Nav from 'components/controls/Nav';
import { Button, Tooltip, Typography, Flex } from 'antd';

import './ResultOption.sass';

export const ResultOption = (props: any) => {
	const { t } = useTranslation();

	const { options = [] } = props;
	const currentOption = options[props.optionIndex % options.length];

	if (!options) {
		return (
			<div className="ResultOption">
				<Typography.Title level={2}>{t("models.option.no_x")}</Typography.Title>
			</div>
		);
	}

	let maxConstraintsScores = {}
	let maxStakefactorsScores = {}
	let maxIndicatorsScores = {}
	const indexedEvaluations = {
		'indicator': keyBy(currentOption.indicatorsEvaluations, 'itemId'),
		'constraint': keyBy(currentOption.constraintsEvaluations, 'itemId'),
		'stakefactor': keyBy(currentOption.stakefactorsEvaluations, 'itemId')
	}

	options.forEach((o: any) => {
		o.constraintsEvaluations.forEach((ev: any) => {
			if (!maxConstraintsScores[ev.itemId]) {
				maxConstraintsScores[ev.itemId] = {
					optionId: o.id,
					value: ev.value
				}
			} else {
				if (ev.value > maxConstraintsScores[ev.itemId].value) {
					maxConstraintsScores[ev.itemId] = {
						optionId: o.id,
						value: ev.value
					}
				}
			}
		})
	});

	options.forEach((o: any) => {
		o.stakefactorsEvaluations.forEach((ev: any) => {
			if (!maxStakefactorsScores[ev.itemId]) {
				maxStakefactorsScores[ev.itemId] = {
					optionId: o.id,
					value: ev.value
				}
			} else {
				if (ev.value > maxStakefactorsScores[ev.itemId].value) {
					maxStakefactorsScores[ev.itemId] = {
						optionId: o.id,
						value: ev.value
					}
				}
			}
		})
	});

	options.forEach((o: any) => {
		o.indicatorsEvaluations.forEach((ev: any) => {
			if (!maxIndicatorsScores[ev.itemId]) {
				maxIndicatorsScores[ev.itemId] = {
					optionId: o.id,
					value: ev.value
				}
			} else {
				if (ev.value > maxIndicatorsScores[ev.itemId].value) {
					maxIndicatorsScores[ev.itemId] = {
						optionId: o.id,
						value: ev.value
					}
				}
			}
		})
	});

	const score = getOptionScore(
		currentOption,
		props.stakefactors,
		props.indicators,
		props.constraints,
		currentOption.stakefactorsEvaluations,
		currentOption.indicatorsEvaluations,
		currentOption.constraintsEvaluations
	);

	return (
		<div className="ResultOption">
			<Nav
				fullWidth
				count={props.options.length}
				options={props.options}
				index={props.optionIndex}
				onSelect={(index: number) => {
					props.setOptionIndex(index);
				}}
			>
				<Typography.Title level={2}>
					<small style={{ marginRight: '0.5rem' }}>{t("models.option.name")} {(props.optionIndex % props.options.length) + 1}:</small>
					{props.options[props.optionIndex % props.options.length].name}{' '}

					<small>
						{props.options[props.optionIndex % props.options.length].isGutDecision ? (
							<div className="GutDecision">
								<FontAwesomeIcon icon={faHeart} />
								Gut decision {props.experienceLevel == 1 ? ' - Not experienced' : null}
								{props.experienceLevel == 2 ? ' - Partially experienced' : null}
								{props.experienceLevel == 3 ? ' - Highly experienced' : null}
							</div>
						) : null}
					</small>

					{currentOption.comments && <Tooltip title={currentOption.comments || t("models.option.messages.no_comments")}>
						<Button
							shape="circle"
							className={currentOption.comments ? 'active comments ml-1' : 'comments ml-1'}
						>
							<FontAwesomeIcon icon={faCommentPlain} style={{ color: "#2EA0DF" }} />
						</Button>
					</Tooltip>
					}
				</Typography.Title>
			</Nav>

			<Slider readOnly={true} value={score.toFixed(2)}></Slider>


			<Flex className="ElementsRank">
				<div className="RankCriteriaColumn">
					<div className="title">{t("models.indicator.name_plural")} <small>{props.score?.indicators !== null ? "(" + props.score?.indicators.toFixed(0) + ")" : ''}</small></div>

					{sortBy(props.indicators, (s: any) => s.name).map(
						(s: any) => (
							s.weight !== 0 ? <div className="RankCriteriaWrapper">
								<div
									className={["RankCriteria", maxIndicatorsScores[s.id]?.optionId === currentOption.id ? "max" : ""].join(" ")}
									style={{
										borderLeftWidth: '8px',
										borderLeftStyle: 'solid',
										borderLeftColor: getScoreColor(
											getItemScore(s, currentOption.indicatorsEvaluations)
										),
									}}
								>
									{s.name}

									{maxIndicatorsScores[s.id]?.optionId === currentOption.id &&
										<Tooltip title={t("models.scenario.best_evaluation")}>
											<FontAwesomeIcon className="trophy ml-05" icon={faTrophy} />
										</Tooltip>}

									{indexedEvaluations['indicator'][s.id]?.description && (
										<Tooltip title={indexedEvaluations['indicator'][s.id].description}>
											<FontAwesomeIcon
												className="comment ml-05"
												icon={faComment}
											/>
										</Tooltip>
									)}
								</div>
							</div> :
								<div className="RankCriteriaWrapper">
									<div
										className="RankCriteria header mt-1"
										style={{
											borderLeftWidth: '0px',
											borderLeftStyle: 'solid',
											borderLeftColor: "transparent"
										}}
									>
										{s.name}
									</div>
								</div>
						)
					)}
				</div>
				<div className="RankCriteriaColumn">
					<div className="title">{t("models.constraint.name_plural")} <small>{props.score?.constraints !== null ? "(" + props.score?.constraints.toFixed(0) + ")" : ''}</small></div>
					{sortBy(props.constraints, (s: any) => s.name).map(
						(s: any) => (
							s.weight !== 0 ? <div className="RankCriteriaWrapper">
								<div
									className={["RankCriteria", maxConstraintsScores[s.id]?.optionId === currentOption.id ? "max" : ""].join(" ")}
									style={{
										borderLeftWidth: '8px',
										borderLeftStyle: 'solid',
										borderLeftColor: getScoreColor(
											getItemScore(s, currentOption.constraintsEvaluations)
										),
									}}
								>
									{s.name}

									{maxConstraintsScores[s.id]?.optionId === currentOption.id &&
										<Tooltip title={t("models.optionEvaluation.best_evaluation")}>
											<FontAwesomeIcon className="trophy ml-05" icon={faTrophy} />
										</Tooltip>}

									{indexedEvaluations['constraint'][s.id]?.description && (
										<Tooltip title={indexedEvaluations['constraint'][s.id].description}>
											<FontAwesomeIcon
												className="comment ml-05"
												icon={faComment}
											/>
										</Tooltip>
									)}
								</div>
							</div> :
								<div className="RankCriteriaWrapper">
									<div
										className="RankCriteria header mt-1"
										style={{
											borderLeftWidth: '0px',
											borderLeftStyle: 'solid',
											borderLeftColor: "transparent"
										}}
									>
										{s.name}
									</div>
								</div>
						)
					)}
				</div>
				<div className="RankCriteriaColumn">
					<div className="title">{t("models.stakefactor.name_plural")} <small>{props.score?.stakefactors !== null ? "(" + props.score?.stakefactors.toFixed(0) + ")" : ''}</small></div>
					{sortBy(
						props.stakefactors,
						(s: any) => s.name
					).map((s: any) => (
						s.weight !== 0 ? <div className="RankCriteriaWrapper">
							<div
								className={["RankCriteria", maxStakefactorsScores[s.id]?.optionId === currentOption.id ? "max" : ""].join(" ")}
								style={{
									borderLeftWidth: '8px',
									borderLeftStyle: 'solid',
									borderLeftColor: getScoreColor(
										getItemScore(s, currentOption.stakefactorsEvaluations)
									),
								}}
							>
								{s.name}

								{maxStakefactorsScores[s.id]?.optionId === currentOption.id &&
									<Tooltip title={t("models.optionEvaluation.best_evaluation")}>
										<FontAwesomeIcon className="trophy ml-05" icon={faTrophy} />
									</Tooltip>}

								{indexedEvaluations['stakefactor'][s.id]?.description && (
									<Tooltip title={indexedEvaluations['stakefactor'][s.id].description}>
										<FontAwesomeIcon
											className="comment ml-05"
											icon={faComment}
										/>
									</Tooltip>
								)}
							</div>
						</div> :
							<div className="RankCriteriaWrapper">
								<div
									className="RankCriteria header mt-1"
									style={{
										borderLeftWidth: '0px',
										borderLeftStyle: 'solid',
										borderLeftColor: "transparent"
									}}
								>
									{s.name}
								</div>
							</div>
					))}
				</div>
			</Flex>

			<Nav
				fullWidth
				count={props.options.length}
				options={props.options}
				index={props.optionIndex}
				onSelect={(index: number) => {
					props.setOptionIndex(index);
				}}
			>
				<div className="d-flex h-100 justify-content-center align-items-center">
					{(props.optionIndex % props.options.length) + 1} / {props.options.length}
				</div>
			</Nav>
		</div>
	);
};
